import { keyBy, pick, snakeCase } from 'lodash/fp';

import { createInputStep } from 'dashboard/lib/workflows';
import { makeStepsConfigUIReady } from 'dashboard/lib/workflows/steps-config-operations';
import { validateWorkflowConfiguration } from 'dashboard/lib/workflows/validation';
import {
  BlockInput,
  BlockOutput,
  BlockParent,
  WorkflowBlock,
} from 'dashboard/models/workflow-block';
import { WorkflowModulePropagationResult } from 'dashboard/models/workflow-module';
import {
  INPUT_STEP_TOKEN,
  OUTPUT_STEP_TOKEN,
  WorkflowStep,
} from 'dashboard/models/workflow-version';
import {
  setInputStep,
  setStepContexts,
  setStepsConfig,
  setWorkflowVersionErrors,
} from 'dashboard/redux/actions/workflow';
import { workflowBlockEndpoints } from 'dashboard/redux/api/workflow-block';
import { workflowModuleEndpoints } from 'dashboard/redux/api/workflow-module';
import {
  getPlatformObjectJsonSchema,
  getUsableObjects,
  getValidationContext,
  getWorkflowSchema,
} from 'dashboard/redux/selectors/workflow-editor-context';
import { BlockData, setBlockData } from 'dashboard/redux/slices/workflow-block';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { generateWorkflowToken } from 'lib/workflows';

export type BlockUpdateData = { 'steps-config': WorkflowStep[] } & BlockData;

export const setBlockEditorContext = (workflowBlock: WorkflowBlock): DashboardThunkAction => {
  return async (dispatch, getState) => {
    dispatch(setBlockData(pick(['description', 'outputs'], workflowBlock.attributes)));
    const inputStep = createInputStep({ inputs: workflowBlock.attributes.inputs });
    dispatch(setInputStep(inputStep));

    const rootState = getState();
    const workflowSchema = getWorkflowSchema(rootState);
    const platformObjectJsonSchema = getPlatformObjectJsonSchema(rootState);
    const showErrorObjects =
      rootState.featureFlags.featureFlags['global:workflow-error-variables'] ?? false;

    if (workflowSchema) {
      const { stepsConfig, stepContexts } = makeStepsConfigUIReady({
        steps: workflowBlock.attributes['steps-config'],
        inputStep,
        workflowSchema,
        platformObjectJsonSchema,
        showErrorObjects,
      });
      dispatch(setStepsConfig(stepsConfig));
      dispatch(setStepContexts(stepContexts));
    }
  };
};

export const saveWorkflowBlock = ({
  id,
  inputs,
  'steps-config': stepsConfig,
  parentType,
  parentId,
  isPublished,
  outputs,
  description,
}: {
  id: string;
  inputs: BlockInput[];
  parentType: BlockParent['type'];
  parentId: string;
  isPublished: boolean;
} & BlockUpdateData): DashboardThunkAction<Promise<string | undefined>> => {
  return async (dispatch) => {
    let workflowBlockId = id;
    if (isPublished) {
      const createBlockQuery = await dispatch(
        workflowBlockEndpoints.createWorkflowBlock.initiate({
          sourceBlockId: workflowBlockId,
          parentType,
          parentId,
        }),
      );

      if ('data' in createBlockQuery) {
        workflowBlockId = createBlockQuery.data.data.id;
      }
    }
    const updateBlockQuery = await dispatch(
      workflowBlockEndpoints.updateWorkflowBlock.initiate({
        id: workflowBlockId,
        inputs,
        stepsConfig,
        description: isPublished ? '' : description,
        outputs,
      }),
    );

    if (!('data' in updateBlockQuery)) {
      return;
    }

    const { data: workflowBlockUpdated } = updateBlockQuery.data;
    dispatch(setBlockEditorContext(workflowBlockUpdated));
    return workflowBlockId;
  };
};

export const publishWorkflowBlock = ({
  id,
  parentType,
  parentId,
  description,
  workflowsToUpdate,
}: {
  id: string;
  parentType: BlockParent['type'];
  parentId: string;
  description: string;
  workflowsToUpdate: string[];
}): DashboardThunkAction<Promise<WorkflowModulePropagationResult[]>> => {
  return async (dispatch) => {
    if (description) {
      await dispatch(
        workflowBlockEndpoints.updateWorkflowBlock.initiate({
          id,
          description,
        }),
      );
    }
    await dispatch(
      workflowBlockEndpoints.publishWorkflowBlock.initiate({
        id,
        parentId,
        parentType,
      }),
    );
    if (workflowsToUpdate.length) {
      const { meta } = await dispatch(
        workflowModuleEndpoints.propagateWorkflowModule.initiate({
          workflowModuleId: parentId,
          workflowIds: workflowsToUpdate,
        }),
      ).unwrap();
      return meta?.results || [];
    }
    return [];
  };
};

export const validateWorkflowBlock = (): DashboardThunkAction<void> => {
  return (dispatch, getState) => {
    const rootState = getState();
    const { blockData } = rootState.workflowBlock;

    const errors = validateWorkflowConfiguration({
      validationContext: getValidationContext(rootState),
      outputs: blockData.outputs,
      externalIntegrationVendors: [],
    });
    dispatch(setWorkflowVersionErrors(errors));
  };
};

export const setStepsConfigAndOutputs = (stepsConfig: WorkflowStep[]): DashboardThunkAction => {
  return async (dispatch, getState) => {
    await dispatch(setStepsConfig(stepsConfig));

    const rootState = getState();
    const { outputs } = rootState.workflowBlock.blockData;
    const usableObjects = getUsableObjects(rootState, OUTPUT_STEP_TOKEN);
    const filteredObjects = usableObjects
      .filter(({ step }) => step.token !== INPUT_STEP_TOKEN)
      .reduce((filtered: Record<string, string>, { step, value, token }) => {
        const stepToken = value.split('.')[0] || token;
        if (!filtered[stepToken]) {
          // We only include the first object for a step so that we ignore associated objects
          // e.g. only include trigger, not trigger.account/trigger.environment/etc
          filtered[stepToken] = snakeCase(step.name);
        }
        return filtered;
      }, {});

    const existingOutputsByConfigToken = keyBy('method-chain', outputs);
    const blockOutputs: BlockOutput[] = Object.entries(filteredObjects).map(
      ([stepToken, name]) => ({
        // reuse output token if it exists
        token: existingOutputsByConfigToken[stepToken]?.token || generateWorkflowToken(),
        name,
        'method-chain': stepToken,
      }),
    );
    dispatch(setBlockData({ outputs: blockOutputs }));
  };
};
